import { connectDeviceSyncStream } from 'services/wms/device-sync.service';
import { EVIDENCE_PHASE, TInspectionEvidencePhase } from 'hooks/useInspectionSessionEvidence';
import { DEVICE_SYNC_SSE_EVENTS } from 'constants/device-sync.constants';
import { IDeviceSyncCompleteReq, TDeviceSyncStreamEventRes } from 'types/wms/device-sync.types';

export interface IBuildSessionEvidenceUrlOptions {
  productId: number;
}

export const buildSessionEvidenceUrl = (sessionId: string, options: IBuildSessionEvidenceUrlOptions): string => {
  try {
    const url = new URL(`${process.env.REACT_APP_WMS}/tool-inspection/session-evidence`);
    url.searchParams.set('sessionId', sessionId);
    url.searchParams.set('productId', String(options.productId));
    return url.toString();
  } catch {
    return '';
  }
};

export const parseDeviceSyncStreamMessage = (rawJson: string): TDeviceSyncStreamEventRes | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson) as unknown;
  } catch {
    return null;
  }

  if (parsed === null || typeof parsed !== 'object') {
    return null;
  }

  const record = parsed as TDeviceSyncStreamEventRes;

  switch (record.status) {
    case DEVICE_SYNC_SSE_EVENTS.PING:
    case DEVICE_SYNC_SSE_EVENTS.EXPIRED:
    case DEVICE_SYNC_SSE_EVENTS.DEVICE_CONNECTED:
    case DEVICE_SYNC_SSE_EVENTS.CANCELLED:
      return record;
    case DEVICE_SYNC_SSE_EVENTS.COMPLETED:
      if (!record.payload) return null;

      return record;
    default:
      return null;
  }
};

interface ISessionEvidenceStreamCallbacks {
  setStreamError: (message: string | null) => void;
  setUiPhase: (phase: TInspectionEvidencePhase) => void;
  setSseCompletedEvidence: (payload: IDeviceSyncCompleteReq | null) => void;
  setStreamReady: (ready: boolean) => void;
}

export const connectSessionEvidenceStream = (
  sessionId: string,
  callbacks: ISessionEvidenceStreamCallbacks
): (() => void) => {
  return connectDeviceSyncStream(sessionId, {
    onOpen: () => {
      callbacks.setStreamError(null);
      callbacks.setStreamReady(true);
    },
    onMessageData: raw => {
      const event = parseDeviceSyncStreamMessage(raw);
      if (!event) return;

      switch (event.status) {
        case DEVICE_SYNC_SSE_EVENTS.PING:
          return;
        case DEVICE_SYNC_SSE_EVENTS.DEVICE_CONNECTED:
          callbacks.setUiPhase(EVIDENCE_PHASE.PhoneActive);
          return;
        case DEVICE_SYNC_SSE_EVENTS.COMPLETED:
          callbacks.setSseCompletedEvidence({ payload: event.payload });
          return;
        case DEVICE_SYNC_SSE_EVENTS.EXPIRED:
          callbacks.setStreamError('La sesión de transferencia expiró.');
          return;
        case DEVICE_SYNC_SSE_EVENTS.CANCELLED:
          callbacks.setStreamError('La sesión de transferencia fue cancelada.');
          return;
        default:
          return;
      }
    },
    onFailure: () => {
      callbacks.setStreamReady(false);
      callbacks.setStreamError('No se pudo conectar el flujo de evidencia. Intenta de nuevo.');
    }
  });
};
